import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { API_BASE_URL, API_ENDPOINTS } from './config';

// Create axios instance with default configuration
const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag to prevent multiple refresh token requests
let isRefreshing = false;
let refreshTimeout: ReturnType<typeof setTimeout> | null = null;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });
  failedQueue = [];
};

// Retry refresh with backoff
const attemptRefresh = async (retries = 3): Promise<{ access: string; refresh?: string }> => {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}${API_ENDPOINTS.auth.refreshToken}`,
        { refresh: refreshToken },
        { timeout: 10000 }
      );
      return response.data;
    } catch (err: any) {
      const status = err?.response?.status;
      // If 401/403 from refresh endpoint, token is permanently invalid — don't retry
      if (status === 401 || status === 403) {
        throw err;
      }
      // For network/timeout errors, retry with backoff
      if (attempt < retries) {
        await new Promise(r => setTimeout(r, 1000 * attempt));
      } else {
        throw err;
      }
    }
  }
  throw new Error('Refresh failed after retries');
};

// Request interceptor - attach access token to requests
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('accessToken');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Attach X-View-As header when viewing as a family member
    const activeProfile = localStorage.getItem('activeProfile');
    if (activeProfile && config.url !== API_ENDPOINTS.customerSupport.chat) {
      try {
        const profile = JSON.parse(activeProfile);
        if (profile.id !== 'self') {
          config.headers['X-View-As'] = String(profile.id);
        }
      } catch {
        // Ignore malformed JSON
      }
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle token refresh on 401
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // If error is not 401 or request has already been retried, reject
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Check if this is a login/register request - don't try to refresh for these
    const isAuthRequest = originalRequest.url?.includes('/api/accounts/password-login') ||
                          originalRequest.url?.includes('/api/accounts/register') ||
                          originalRequest.url?.includes('/api/accounts/otp-login') ||
                          originalRequest.url?.includes('/api/accounts/token/refresh');

    if (isAuthRequest) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      // If already refreshing, queue this request
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          return axiosInstance(originalRequest);
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    // Safety timeout — reset isRefreshing if refresh hangs
    refreshTimeout = setTimeout(() => {
      isRefreshing = false;
      processQueue(new axios.AxiosError('Refresh timeout') as AxiosError, null);
    }, 15000);

    try {
      const data = await attemptRefresh();

      // Store new access token
      localStorage.setItem('accessToken', data.access);

      // Store rotated refresh token if returned (rotation is enabled)
      if (data.refresh) {
        localStorage.setItem('refreshToken', data.refresh);
      }

      // Process queued requests
      processQueue(null, data.access);

      // Retry original request with new token
      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${data.access}`;
      }

      return axiosInstance(originalRequest);
    } catch (refreshError: any) {
      const status = refreshError?.response?.status;

      // Only force logout if the refresh token is definitively invalid (401/403)
      if (status === 401 || status === 403) {
        processQueue(refreshError as AxiosError, null);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        // Redirect to CS login if on CS portal, otherwise main login
        const isCSPortal = window.location.pathname.startsWith('/cs');
        window.location.href = isCSPortal ? '/cs-login' : '/login';
      } else {
        // For network errors, just reject — don't force logout
        processQueue(refreshError as AxiosError, null);
      }

      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
      if (refreshTimeout) {
        clearTimeout(refreshTimeout);
        refreshTimeout = null;
      }
    }
  }
);

export default axiosInstance;

/**
 * Pull the first human-readable message out of a DRF error response.
 *
 * DRF replies with either {"detail": "..."} or per-field errors such as
 * {"date": ["An entry for this date already exists."]}. Returns the fallback
 * when the response carries nothing usable.
 */
export const getApiErrorMessage = (error: unknown, fallback: string): string => {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (!data) return fallback;
  if (typeof data === 'string') return data;

  const firstMessage = (value: unknown): string | null => {
    if (!value) return null;
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) {
      for (const item of value) {
        const message = firstMessage(item);
        if (message) return message;
      }
      return null;
    }
    if (typeof value === 'object') {
      const record = value as Record<string, unknown>;
      if (typeof record.detail === 'string') return record.detail;
      for (const key of Object.keys(record)) {
        const message = firstMessage(record[key]);
        if (message) return message;
      }
    }
    return null;
  };

  return firstMessage(data) || fallback;
};

// Export a non-authenticated axios instance for public endpoints
export const publicAxios = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});
